import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radio, Youtube } from "lucide-react";
import { api } from "@/lib/api";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { IMAGES } from "@/data/content";

function getYoutubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0` : null;
}

export default function LiveSection() {
  const [live, setLive] = useState({ status: "offline", message: "Nenhuma transmissão ao vivo neste momento.", url: "" });
  const isLive = live.status === "online";
  const embedUrl = isLive ? getYoutubeEmbed(live.url) : null;

  useEffect(() => {
    api.get("/live").then((r) => setLive(r.data)).catch(() => {});
  }, []);

  return (
    <section id="ao-vivo" className="bg-[#2E070D] px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
        className="relative overflow-hidden border border-[#C5A059]/25"
      >
        <img src={IMAGES.candles} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        {embedUrl && (
          <div data-testid="live-embed-player" className="relative aspect-video w-full border-b border-[#C5A059]/25 bg-black">
            <iframe
              src={embedUrl}
              title="DACB ao Vivo"
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
        <div className="relative px-6 sm:px-12 lg:px-20 py-16 lg:py-20 flex flex-col lg:flex-row lg:items-center gap-10">
          <div className="flex-1">
            <motion.div variants={fadeUp} className="flex items-center gap-4">
              <Radio className={`h-6 w-6 ${isLive ? "text-[#16A34A]" : "text-[#D97706]"}`} />
              <span
                data-testid="live-stream-status-badge"
                className={`overline-label px-4 py-1.5 border ${
                  isLive ? "border-[#16A34A] text-[#16A34A] animate-pulse-soft" : "border-[#D97706]/60 text-[#D97706]"
                }`}
              >
                {isLive ? "Ao Vivo" : "Offline"}
              </span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold uppercase text-[#FBF9F4]">
              DACB ao Vivo
            </motion.h2>
            <motion.p variants={fadeUp} data-testid="live-status-message" className="font-liturgy mt-4 text-xl italic text-[#FBF9F4]/75">
              {live.message || "Nenhuma transmissão ao vivo neste momento."}
            </motion.p>
          </div>
          <motion.a
            variants={fadeUp}
            data-testid="live-transmissions-button"
            href={live.url || "https://www.youtube.com/"}
            target="_blank"
            rel="noreferrer"
            className="overline-label inline-flex items-center gap-3 self-start bg-[#C5A059] px-8 py-4 text-[#2E070D] hover:bg-[#D4AF37] transition-colors"
          >
            <Youtube className="h-4 w-4" /> Ir para as transmissões
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
}
